import type { TemplateStatus } from "@genz/platform";

/**
 * Task 13 (C5) — ONE honest vocabulary for the save/submit outcome, derived from the
 * status the cloud actually returned (never hardcoded from the local approvals guess).
 *
 * When the tenant's approval gate is OFF, `submit_version` auto-approves → the version
 * comes back **Active**; when the gate is ON it stays **Pending**. The Wizard success
 * screen and the TemplateEditor "saved" line both render this so the copy can never
 * claim "Active and locked" while the version is really waiting for review.
 *
 * The chip class mirrors the templates-list conventions (`badge--success` for Active,
 * `badge--warn` for Pending) so the status reads the same everywhere.
 */
export interface SaveStatusCopy {
  /** Short status chip label. */
  chipLabel: string;
  /** Badge class mirroring the templates list (Active → success, Pending → warn). */
  chipClass: string;
  /** Primary line (Wizard heading / editor compact line). Carries the honest verb. */
  headline: string;
  /** Supporting sentence for the Wizard success screen. */
  detail: string;
}

/**
 * Map the returned version status to honest success copy. Any activating status
 * (Active — and, defensively, Approved) reads "saved & Active"; Pending reads
 * "submitted — pending approval".
 */
export function saveStatusCopy(status: TemplateStatus): SaveStatusCopy {
  if (status === "Pending") {
    return {
      chipLabel: "Pending approval",
      chipClass: "badge badge--warn",
      headline: "Submitted — pending approval.",
      detail: "An admin needs to approve it before it becomes active.",
    };
  }
  // Intentional collapse: submitVersion only ever returns Active or Pending, so any status that
  // isn't Pending is treated as Active here (Approved and other activating statuses read as Active).
  return {
    chipLabel: "Active",
    chipClass: "badge badge--success",
    headline: "Template saved — it’s Active and locked.",
    detail: "Create a new version to make changes.",
  };
}
